/** Shared Suppliers + Invoices drill-down column used by Flash Report and other views.
 *  v5: "Top Purchases by Value" = top individual LINE ITEMS within the active
 *  category (food/beverage), not top whole invoices. Each row shows the line
 *  description, parent supplier, amount and % share.
 *  v5: inline supplier insight icon — when an insight is present for a supplier
 *  an exclamation icon appears next to their name with a hover tooltip showing
 *  the dynamic commentary message.
 */
import { useMemo, useState, Fragment } from "react";
import { fmtGBP, fmtGBP2, fmtDateUK, fmtDayLongUK } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Warning, CaretLeft, CaretRight, CaretDown, CaretUp } from "@phosphor-icons/react";

const PAGE_SIZE = 15;

export default function PurchasesColumn({
  testid,
  title,
  subtitle,
  accent,
  amountKey,
  classification,        // "DRY" | "WET" — used to filter line items
  invoices,
  allInvoices,           // ALL invoices in the period — scanned for top line items
  suppliers,
  insightsBySupplier,    // { [supplier]: { variance_pct, message, severity } }
  newSuppliers,          // Set of supplier names whose first-ever bill is in this period
  totalSales = 0,        // current-period total net sales — drives "% of sales"
  onSelect,
}) {
  const grandTotal = suppliers.reduce((s, x) => s + x.total, 0);
  const grandPctOfSales = totalSales ? (grandTotal / totalSales) * 100 : 0;
  const scanInvoices = allInvoices || invoices;

  // Flatten line items for the active category and rank by line_amount
  const topLineItems = (() => {
    const flat = [];
    for (const inv of scanInvoices) {
      for (const li of inv.line_items || []) {
        if (li.classification !== classification) continue;
        const amt = li.line_amount || 0;
        if (amt <= 0) continue;
        flat.push({
          key: `${inv.invoice_id}-${li.account_code}-${li.description}-${amt}`,
          supplier: inv.contact_name,
          invoice_id: inv.invoice_id,
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          description: li.description || li.account_code || "—",
          amount: amt,
          parent_invoice: inv,
        });
      }
    }
    return flat.sort((a, b) => b.amount - a.amount).slice(0, 8);
  })();

  const balanceMap = (() => {
    const sortedAsc = [...invoices].sort((a, b) => (a.invoice_date || "").localeCompare(b.invoice_date || ""));
    let running = 0;
    const map = {};
    for (const inv of sortedAsc) {
      const amt = inv[amountKey] || inv.total || 0;
      running += amt;
      map[inv.invoice_id] = running;
    }
    return map;
  })();

  return (
    <TooltipProvider delayDuration={120}>
      <div className="bg-white border border-[#E2E8F0] rounded-md card-fade flex flex-col" data-testid={testid}>
        <div className="p-6 border-b border-[#EEEEEE]">
          <p className="uppercase text-[11px] tracking-[0.2em] font-medium" style={{ color: accent }}>{subtitle}</p>
          <div className="flex items-baseline justify-between mt-1">
            <h3 className="font-display text-2xl tracking-tight">{title}</h3>
            <div className="text-right">
              <span className="font-mono-num text-lg" style={{ color: accent }}>{fmtGBP(grandTotal)}</span>
              {totalSales > 0 && (
                <p data-testid={`${testid}-pct-of-sales`} className="text-[11px] font-mono text-[#94A3B8] uppercase tracking-[0.18em] mt-0.5">
                  {grandPctOfSales.toFixed(1)}% of sales
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#EEEEEE] border-b border-[#EEEEEE]">
          {/* LEFT — Cost of Sales by Supplier */}
          <div className="p-4 sm:p-6 pb-5">
            <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-4">Cost of Sales by Supplier</p>
            {suppliers.length === 0 ? (
              <p className="text-xs text-[#94A3B8] py-2">No supplier spend recorded for this period.</p>
            ) : (
              <div className="space-y-3" data-testid={`${testid}-suppliers`}>
                {[...suppliers.slice(0, 8), ...suppliers.slice(8).filter((x) => newSuppliers?.has(x.supplier))].map((s) => {
                  const pct = grandTotal ? (s.total / grandTotal) * 100 : 0;
                  const pctOfSales = totalSales ? (s.total / totalSales) * 100 : 0;
                  const insight = insightsBySupplier?.[s.supplier];
                  return (
                    <div key={s.supplier} className="space-y-1.5">
                      <div className="flex items-baseline justify-between gap-3 text-xs">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {insight && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  data-testid={`supplier-insight-${s.supplier.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                  className={`shrink-0 ${insight.severity === "high" ? "text-rose-700" : "text-amber-700"} hover:opacity-80`}
                                  aria-label={`Variance alert: ${insight.message}`}
                                >
                                  <Warning size={13} weight="fill" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                align="start"
                                className={`max-w-xs p-3 rounded-md border ${insight.severity === "high" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
                              >
                                <p className="uppercase text-[11px] tracking-[0.2em] font-medium mb-1.5" style={{ color: insight.severity === "high" ? "#9F1239" : "#A16207" }}>
                                  +{insight.variance_pct}% vs 3-period baseline
                                </p>
                                <p className="text-[11px] leading-relaxed">{insight.message}</p>
                                <p className="text-[11px] font-mono mt-1.5 opacity-70">
                                  Current £{insight.current_spend.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                                  {" · "}
                                  Baseline £{insight.baseline_spend.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                                  {" · "}
                                  {insight.share_of_category_pct}% of category
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <span className="truncate font-medium text-[#0F172A]">{s.supplier}</span>
                          {newSuppliers?.has(s.supplier) && (
                            <span title="First ever bill from this supplier is in this period" data-testid={`new-supplier-tag-${s.supplier.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className="shrink-0 rounded-full bg-[#DCFCE7] px-1.5 py-px text-[10px] font-bold tracking-wide text-[#15803D]">NEW</span>
                          )}
                        </div>
                        <span className="font-mono-num shrink-0 text-right">
                          <span>{fmtGBP(s.total)}</span>
                          <span className="text-[#94A3B8] ml-2">{pct.toFixed(1)}%</span>
                          {totalSales > 0 && (
                            <span data-testid={`supplier-pct-of-sales-${s.supplier.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="block text-[11px] text-[#94A3B8] font-mono mt-0.5">
                              {pctOfSales.toFixed(2)}% of sales
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#EEEEEE] rounded overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${pct}%`, background: accent }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT — Top Line Items by Value */}
          <div className="p-4 sm:p-6 pb-5">
            <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-4">Top Purchases by Value</p>
            {topLineItems.length === 0 ? (
              <p className="text-xs text-[#94A3B8] py-2">No itemised line activity in this period.</p>
            ) : (
              <div className="space-y-3" data-testid={`${testid}-top-line-items`}>
                {topLineItems.map((li) => {
                  const pct = grandTotal ? (li.amount / grandTotal) * 100 : 0;
                  return (
                    <div
                      key={li.key}
                      className="space-y-1.5 cursor-pointer group"
                      onClick={() => onSelect?.(li.parent_invoice)}
                      data-testid={`top-line-${li.invoice_id}`}
                    >
                      <div className="flex items-baseline justify-between gap-3 text-xs">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[#0F172A] group-hover:underline" title={li.description}>{li.description}</p>
                          <p className="text-[11px] text-[#94A3B8] font-mono truncate mt-0.5">
                            {li.supplier} · {fmtDateUK(li.invoice_date)} · {li.invoice_number}
                          </p>
                        </div>
                        <span className="font-mono-num shrink-0">
                          {fmtGBP(li.amount)}
                          <span className="text-[#94A3B8] ml-2">{pct.toFixed(1)}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#EEEEEE] rounded overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${pct}%`, background: accent }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <InvoicesSection
          testid={testid}
          newSuppliers={newSuppliers}
          invoices={invoices}
          amountKey={amountKey}
          accent={accent}
          balanceMap={balanceMap}
          grandTotal={grandTotal}
          onSelect={onSelect}
        />
      </div>
    </TooltipProvider>
  );
}

function InvoicesSection({ testid, newSuppliers, invoices, amountKey, accent, balanceMap, grandTotal, onSelect }) {
  const [mode, setMode] = useState("flat");  // "flat" | "supplier" | "day" | "week"
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});

  // Sorted flat list — newest first
  const sortedFlat = useMemo(
    () => [...invoices].sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || "")),
    [invoices]
  );
  const totalPages = Math.max(1, Math.ceil(sortedFlat.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageRows = sortedFlat.slice(pageStart, pageStart + PAGE_SIZE);

  // Grouped buckets per mode
  const grouped = useMemo(() => {
    if (mode === "flat") return [];
    const m = new Map();
    for (const inv of sortedFlat) {
      const amt = inv[amountKey] || inv.total || 0;
      let bucketKey, bucketLabel;
      if (mode === "supplier") {
        bucketKey = inv.contact_name || "Unknown";
        bucketLabel = bucketKey;
      } else if (mode === "day") {
        bucketKey = inv.invoice_date || "—";
        bucketLabel = inv.invoice_date ? fmtDayLongUK(inv.invoice_date) : "—";
      } else if (mode === "week") {
        // ISO week starting Monday — key by Monday date
        if (!inv.invoice_date) { bucketKey = "—"; bucketLabel = "—"; }
        else {
          const d = new Date(inv.invoice_date);
          const day = d.getDay();
          const mondayOffset = day === 0 ? -6 : 1 - day;
          const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + mondayOffset);
          const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
          const ymdLocal = (x) => `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,"0")}-${String(x.getDate()).padStart(2,"0")}`;
          bucketKey = ymdLocal(monday);
          bucketLabel = `Wk of ${fmtDateUK(bucketKey)} → ${fmtDateUK(ymdLocal(sunday))}`;
        }
      }
      const g = m.get(bucketKey) || { key: bucketKey, label: bucketLabel, total: 0, count: 0, invoices: [] };
      g.total += amt;
      g.count += 1;
      g.invoices.push(inv);
      m.set(bucketKey, g);
    }
    const arr = Array.from(m.values());
    // Sort: supplier by total desc; day/week by date desc
    if (mode === "supplier") arr.sort((a, b) => b.total - a.total);
    else arr.sort((a, b) => b.key.localeCompare(a.key));
    return arr;
  }, [sortedFlat, mode, amountKey]);

  const switchMode = (m) => { setMode(m); setPage(1); setExpanded({}); };

  const MODES = [
    { v: "flat", l: "Flat" },
    { v: "supplier", l: "Supplier" },
    { v: "day", l: "Day" },
    { v: "week", l: "Week" },
  ];

  return (
    <div className="overflow-x-auto flex-1" data-testid={`${testid}-invoices`}>
      <div className="flex items-center justify-between px-6 pt-5 pb-3 gap-3 flex-wrap">
        <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium">
          Invoices · {invoices.length}
        </p>
        <div className="flex items-center bg-[#EEEEEE] rounded-md p-0.5" data-testid={`${testid}-invoices-mode`}>
          {MODES.map((m) => (
            <button
              key={m.v}
              onClick={() => switchMode(m.v)}
              data-testid={`${testid}-invoices-${m.v}`}
              className={`px-2.5 py-1.5 rounded text-[11px] font-medium uppercase tracking-wider transition-colors ${
                mode === m.v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#475569] hover:text-[#0F172A]"
              }`}
            >{m.l}</button>
          ))}
        </div>
      </div>

      {invoices.length === 0 ? (
        <p className="text-xs text-[#94A3B8] px-6 pb-6">No invoices for this period.</p>
      ) : mode === "flat" ? (
        <>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] px-4 sm:px-6 h-9 hidden sm:table-cell">Invoice</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-9 max-sm:pl-4">Supplier</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-9">Date</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-9 max-sm:pr-4">Amount</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-9 hidden sm:table-cell">% of spend</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right pr-6 h-9 hidden sm:table-cell">Running Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((r) => {
                const amt = r[amountKey] || r.total || 0;
                const pct = grandTotal ? (amt / grandTotal) * 100 : 0;
                return (
                  <TableRow key={r.invoice_id} onClick={() => onSelect?.(r)} className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]" data-testid={`invoice-row-${r.invoice_id}`}>
                    <TableCell className="font-mono-num text-xs px-4 sm:px-6 py-2.5 hidden sm:table-cell">{r.invoice_number}</TableCell>
                    <TableCell className="text-xs py-2.5 max-sm:max-w-[150px] max-sm:truncate max-sm:pl-4">{r.contact_name}</TableCell>
                    <TableCell className="font-mono-num text-xs py-2.5 whitespace-nowrap">{fmtDateUK(r.invoice_date)}</TableCell>
                    <TableCell className="text-right font-mono-num text-xs py-2.5 max-sm:pr-4 whitespace-nowrap" style={{ color: accent }}>{fmtGBP2(amt)}</TableCell>
                    <TableCell className="text-right font-mono-num text-xs py-2.5 text-[#475569] hidden sm:table-cell">{pct.toFixed(1)}%</TableCell>
                    <TableCell className="text-right font-mono-num text-xs pr-6 py-2.5 text-[#0F172A] font-medium hidden sm:table-cell">{fmtGBP2(balanceMap[r.invoice_id] || 0)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination testid={testid} page={safePage} totalPages={totalPages} total={sortedFlat.length} onPage={setPage} />
        </>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-[#E2E8F0]">
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] px-6 h-9" />
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-9">
                {mode === "supplier" ? "Supplier" : mode === "day" ? "Date" : "Week"}
              </TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-9">Invoices</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-9 max-sm:pr-4">Total</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right pr-6 h-9 hidden sm:table-cell">% of spend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grouped.map((g) => {
              const isOpen = !!expanded[g.key];
              const pct = grandTotal ? (g.total / grandTotal) * 100 : 0;
              return (
                <Fragment key={g.key}>
                  <TableRow
                    onClick={() => setExpanded((s) => ({ ...s, [g.key]: !s[g.key] }))}
                    className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]"
                    data-testid={`${testid}-group-${g.key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    <TableCell className="px-6 py-2.5 w-8 text-[#475569]">
                      {isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </TableCell>
                    <TableCell className="text-xs py-2.5 font-medium text-[#0F172A]">
                      {g.label}
                      {newSuppliers?.has(g.label) && (
                        <span className="ml-2 rounded-full bg-[#DCFCE7] px-1.5 py-px text-[10px] font-bold tracking-wide text-[#15803D]" data-testid={`${testid}-group-new-tag`}>NEW</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-xs py-2.5 text-[#475569]">{g.count}</TableCell>
                    <TableCell className="text-right font-mono-num text-xs py-2.5 font-medium max-sm:pr-4 whitespace-nowrap" style={{ color: accent }}>{fmtGBP2(g.total)}</TableCell>
                    <TableCell className="text-right font-mono-num text-xs pr-6 py-2.5 text-[#475569] hidden sm:table-cell">{pct.toFixed(1)}%</TableCell>
                  </TableRow>
                  {isOpen && g.invoices.map((r) => {
                    const amt = r[amountKey] || r.total || 0;
                    return (
                      <TableRow key={r.invoice_id} onClick={(e) => { e.stopPropagation(); onSelect?.(r); }} className="border-[#F5F5F5] bg-[#F5F5F5] cursor-pointer hover:bg-[#EEEEEE]" data-testid={`grouped-invoice-row-${r.invoice_id}`}>
                        <TableCell />
                        <TableCell className="text-[11px] py-1.5 pl-2 text-[#475569]">
                          <span className="font-mono-num">{r.invoice_number}</span>
                          <span className="text-[#94A3B8] ml-2">{mode === "supplier" ? fmtDateUK(r.invoice_date) : r.contact_name}</span>
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right font-mono-num text-[11px] py-1.5" style={{ color: accent }}>{fmtGBP2(amt)}</TableCell>
                        <TableCell className="text-right font-mono-num text-[11px] pr-6 py-1.5 text-[#94A3B8] hidden sm:table-cell">
                          {grandTotal ? ((amt / grandTotal) * 100).toFixed(1) : "0.0"}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function Pagination({ testid, page, totalPages, total, onPage }) {
  if (totalPages <= 1) {
    return (
      <p className="px-6 py-3 text-[11px] font-mono uppercase tracking-wider text-[#94A3B8]">
        {total} invoice{total === 1 ? "" : "s"}
      </p>
    );
  }
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-[#EEEEEE]" data-testid={`${testid}-pagination`}>
      <p className="text-[11px] font-mono uppercase tracking-wider text-[#94A3B8]">
        Page {page} of {totalPages} · {total} invoices
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1}
          data-testid={`${testid}-page-prev`}
          className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wider text-[#475569] hover:bg-[#EEEEEE] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CaretLeft size={11} weight="bold" /> Prev
        </button>
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          data-testid={`${testid}-page-next`}
          className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wider text-[#475569] hover:bg-[#EEEEEE] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <CaretRight size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}
